// src/ExpandableList.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
	analyseImage,
	updateDescription,
	uploadAttachment,
	updateClaimant,
	analyzeSnapshotIfNeeded,
} from "./utility/api";
import {
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Collapse,
	Dialog,
	IconButton,
	Typography,
	Button,
	List,
	ListItem,
	Container,
	AppBar,
	Toolbar,
	Avatar,
	Box,
	Grid2,
	Drawer,
	Fab,
	CircularProgress,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import logo from "./Malayan.png";
import AgoraVideoCallWithSnapshot from "./AgoraVideoCallWithSnapshot";
import { setUser } from "./features/UserSlice";
import {
	setAssignment,
	createDocumentAsync,
	updateAssignmentAsync,
} from "./features/AssignmentSlice";
import { encodeImageToBase64 } from "./utility/api";
// import backgroundImage from "./background.jpeg";
import {
	fetchUsers,
	fetchAssignments,
	updateAssignment,
	updateAssignmentStatus,
	sendMessage,
	fetchAttachmentList,
	uploadSnapshots,
	uploadSignature,
	getInsuranceCompanies,
} from "./utility/api";
import {
	toggleDrawer,
	sideMenuList,
	base64ToBlob,
	convertToPng,
	// extractDimensions2,
	formatPhoneNumber,
	dataURLToBlob,
	numberToWords,
} from "./utility/utils";
import NotifyClaimantDialog from "./component/NotifyClaimantDialog";
import AssignAdjusterDialog from "./component/AssignAdjusterDialog";
import InvoiceDialog from "./component/InvoiceDialog";
import ImagePreviewDialog from "./component/ImagePreviewDialog";
import SignatureDialog from "./component/SignatureDialog";
import FilterPanel from "./component/FilterPanel";
import AssignmentDetailsSection from "./component/AssignmentDetailsSection";
import SnapshotCard from "./component/SnapshotCard";
import { store } from "./app/store";
import { getUnsavedSnapshots } from "./utility/claim-workflow";

function ExpandableList() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [expanded, setExpanded] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [adjusterDialogOpen, setAdjusterDialogOpen] = useState(false);
	const [currentContact, setCurrentContact] = useState("");
	const [errors, setErrors] = useState({});
	const [joined, setJoined] = useState(false);
	const [isShowInvoice, setIsShowInvoice] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [users, setUsers] = useState([]);
	const [assignments, setAssignments] = useState([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [snapshots, setSnapshots] = useState({});
	const [editedDescriptions, setEditedDescriptions] = useState({});
	const [editedCustomDescriptions, setEditedCustomDescriptions] = useState(
		{},
	);
	const [editedHeights, setEditedHeights] = useState({});
	const [editedWidths, setEditedWidths] = useState({});
	const [attachments, setAttachments] = useState({});
	const fileInputRef = useRef(null);
	const [addedSnapshots, setAddedSnapshots] = useState({});
	const [assignedAdjuster, setAssignedAdjuster] = useState(null);
	const [selectedImage, setSelectedImage] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [imageDialogOpen, setImageDialogOpen] = useState(false); // State to manage image dialog
	const validateMobile = (mobileNumber) => /^[0-9]{9,10}$/.test(mobileNumber);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedClaimantIdx, setSelectedClaimantIdx] = useState(0);
	const [filterStatus, setFilterStatus] = useState({
		"Select All": true,
		NEW: true,
		Assigned: true,
		InProgress: true,
		Review: true,
		Approved: true,
		Completed: true,
	});

	// Insurance company filter state
	const [insuranceCompanies, setInsuranceCompanies] = useState([]); // [{ userName, name }]
	const [filterCompany, setFilterCompany] = useState({ "Select All": true });

	// Load insurance companies once
	useEffect(() => {
		let isMounted = true;
		const loadCompanies = async () => {
			try {
				const list = await getInsuranceCompanies();
				if (!isMounted) return;
				setInsuranceCompanies(list || []);
			} catch (err) {
				console.error("Failed to fetch insurance companies", err);
			}
		};
		loadCompanies();
		return () => {
			isMounted = false;
		};
	}, []);

	// Keep filterCompany keys in sync with available companies (API union assignments)
	useEffect(() => {
		const companyNamesFromApi = (insuranceCompanies || [])
			.map((c) => c.name)
			.filter(Boolean);
		const companyNamesFromAssignments = (assignments || [])
			.map((a) => a.insuranceCompany)
			.filter(Boolean);
		const allNames = Array.from(
			new Set([...companyNamesFromApi, ...companyNamesFromAssignments]),
		).sort();

		setFilterCompany((prev) => {
			// Start with existing or base with Select All preserved
			const next = { ...prev };
			if (next["Select All"] === undefined) next["Select All"] = true;

			// Add any missing companies defaulting to current Select All value
			for (const name of allNames) {
				if (next[name] === undefined) {
					next[name] = !!next["Select All"]; // follow Select All
				}
			}

			// Remove stale company keys not in the list
			Object.keys(next)
				.filter((k) => k !== "Select All" && !allNames.includes(k))
				.forEach((k) => delete next[k]);

			return { ...next };
		});
	}, [insuranceCompanies, assignments]);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isShowCanvas, setIsShowCanvas] = useState(false);
	const user = useSelector((state) => state.user.user);
	const currentAssignmentRef = useRef(null);
	let currentAssignment = useSelector((state) => state.assignment.assignment);
	const docIdRef = useRef(null);
	const [, setIsLoading] = useState(false);

	const AssignAdjusterToAssignment = useCallback(async () => {
		const updatedAssignment = {
			...currentAssignment,
			status: "Assigned",
			assignedAdjuster: assignedAdjuster,
		};

		const updatedAssignmentList = await updateAssignment(
			updatedAssignment,
			user,
		);
		setAssignments(updatedAssignmentList);
		dispatch(setAssignment(updatedAssignment));
		setAdjusterDialogOpen(false);
	}, [
		currentAssignment,
		assignedAdjuster,
		user,
		dispatch,
		setAssignments,
		setAdjusterDialogOpen,
	]);

	const getAssignments = useCallback(
		async (isRefresh) => {
			try {
				if (user) {
					const data = await fetchAssignments(user);
					if (!data || data.length === 0) {
						console.warn("No assignments found.");
						return;
					}
					setAssignments(data);
					let response = null;
					if (!isRefresh) {
						dispatch(setAssignment(data[0]));
						currentAssignmentRef.current = data[0];
						response = await fetchAttachmentList(
							data[0].assignmentName,
						);
					} else {
						currentAssignmentRef.current =
							store.getState().assignment.assignment;
						response = await fetchAttachmentList(
							currentAssignmentRef.current.assignmentName,
						);
					}

					// Safeguard against null or undefined response
					const pdfFiles =
						response && response.length > 0
							? response
									.filter((data) =>
										data.fileName.endsWith(".pdf"),
									)
									.map((data) => data.fileName)
							: []; // Default to an empty array if response or fileName is invalid

					const pngFiles =
						response && response.length > 0
							? response
									.filter((data) =>
										data.fileName.endsWith(".png"),
									)
									.map((data) => ({
										imageData: `${process.env.REACT_APP_API_URL}/GetAttachment?assignmentName=${currentAssignmentRef.current.assignmentName}&attachmentName=${data.fileName}`,
										height: data.height, // Changed from data.Height to data.height
										width: data.width,
										description: data.description,
										customDescription:
											data.customDescription,
										isSaved: true, // Add this property for existing snapshots
									}))
							: []; // Default to an empty array if response or fileName is invalid

					setAttachments((prevAttachments) => ({
						...prevAttachments,
						[currentAssignmentRef.current.assignmentName]: pdfFiles,
					}));
					if (
						!snapshots?.[
							currentAssignmentRef.current.assignmentName
						]
					) {
						setSnapshots((prevSnapshots) => ({
							...prevSnapshots,
							[currentAssignmentRef.current.assignmentName]:
								pngFiles, // Attach only .png files
						}));
					}
				}
			} catch (error) {
				console.error("error : ", error);
				alert("Failed to fetch assignments. Please try again.");
			}
		},
		[user, dispatch, snapshots],
	); // Add dependencies

	const fetchAdjusters = useCallback(async () => {
		setLoadingUsers(true);
		try {
			if (users.length === 0) {
				const data = await fetchUsers("Adjuster");
				setUsers(data);
			}
		} catch (error) {
			alert("Failed to fetch reporting managers. Please try again.");
		} finally {
			setLoadingUsers(false);
		}
	}, [users.length]); // Add dependency

	useEffect(() => {
		if (assignments.length === 0) {
			getAssignments(false);
		}
		if (user.role === "Manager" || user.role === "SysAdmin") {
			fetchAdjusters();
		} else {
			setUsers([]);
		}
		if (!user.isSignatureAvailable && user.role !== "SysAdmin") {
			setIsShowCanvas(true);
		}

		const interval = setInterval(() => {
			getAssignments(true); // Fetch every 15 seconds
		}, 15000);

		return () => clearInterval(interval); // Cleanup interval on unmount
	}, [
		user.role,
		user.isSignatureAvailable,
		assignments,
		fetchAdjusters,
		getAssignments,
		currentAssignment,
	]); // Removed unnecessary dependencies

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
	};
	const saveDescription = async (assignmentName, index, snapshot) => {
		try {
			const key = `${assignmentName}-${index}`;
			const updatedDescription = editedDescriptions[key];
			const updatedCustomDescription = editedCustomDescriptions[key];
			const updatedHeight = editedHeights[key];
			const updatedWidth = editedWidths[key];

			// Check if there are any changes to save
			if (
				!updatedDescription &&
				!updatedCustomDescription &&
				updatedHeight === undefined &&
				updatedWidth === undefined
			) {
				console.warn("No changes to save");
				return;
			}

			// Create an updated snapshot object with any changed values
			const snapshotWithChanges = {
				...snapshot,
				description:
					updatedDescription !== undefined
						? updatedDescription
						: snapshot.description,
				customDescription:
					updatedCustomDescription !== undefined
						? updatedCustomDescription
						: snapshot.customDescription,
				height:
					updatedHeight !== undefined
						? updatedHeight
						: snapshot.height,
				width:
					updatedWidth !== undefined ? updatedWidth : snapshot.width,
			};

			await updateDescription(
				assignmentName,
				snapshotWithChanges,
				updatedDescription !== undefined
					? updatedDescription
					: snapshot.description,
			);

			setSnapshots((prevSnapshots) => {
				const updatedSnapshots = { ...prevSnapshots };
				if (!updatedSnapshots[assignmentName]) {
					return prevSnapshots;
				}

				// Only update the specific snapshot
				updatedSnapshots[assignmentName] = updatedSnapshots[
					assignmentName
				].map((snap, i) => {
					if (i === index) {
						return {
							...snap,
							description:
								updatedDescription !== undefined
									? updatedDescription
									: snap.description,
							customDescription:
								updatedCustomDescription !== undefined
									? updatedCustomDescription
									: snap.customDescription,
							height:
								updatedHeight !== undefined
									? updatedHeight
									: snap.height,
							width:
								updatedWidth !== undefined
									? updatedWidth
									: snap.width,
							isSaved: true, // Mark as saved in database
						};
					}
					return snap;
				});

				return updatedSnapshots;
			});

			// Clear all edited fields for this snapshot
			setEditedDescriptions((prev) => {
				const updatedEdits = { ...prev };
				delete updatedEdits[key];
				return updatedEdits;
			});

			setEditedCustomDescriptions((prev) => {
				const updatedEdits = { ...prev };
				delete updatedEdits[key];
				return updatedEdits;
			});

			setEditedHeights((prev) => {
				const updatedEdits = { ...prev };
				delete updatedEdits[key];
				return updatedEdits;
			});

			setEditedWidths((prev) => {
				const updatedEdits = { ...prev };
				delete updatedEdits[key];
				return updatedEdits;
			});

			alert("Changes saved successfully.");
		} catch (error) {
			console.error("Error updating snapshot data:", error);
			alert("Failed to update snapshot data. Please try again.");
		}
	};

	const handleNotifyYesClick = async (e) => {
		setIsSubmitting(true);
		e.preventDefault();
		let validationErrors = {};
		const mobileNumber = formatPhoneNumber(currentContact);

		if (!mobileNumber.trim() || !validateMobile(mobileNumber))
			validationErrors.mobileNumber =
				"Valid 9 or 10-digit mobile number is required.";

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		const claimant = currentAssignment.claimants[selectedClaimantIdx];
		const updatedClaimant = {
			...claimant,
			insuredContactDetails: "+60" + mobileNumber,
			lastUpdatedDatetime: new Date().toISOString(),
			lastUpdatedBy: user.userName,
		};

		try {
			await updateClaimant([updatedClaimant]);
			// Optionally update assignment state here if needed
			await sendMessage(
				currentAssignment.assignmentName,
				"NotifyClaimant",
				claimant.partyId,
			);

			const updatedAssignmentList = await updateAssignmentStatus(
				currentAssignment.assignmentName,
				"InProgress",
				user,
			);
			setAssignments(updatedAssignmentList);

			setDialogOpen(false);
			alert(
				"Claimant Notified via Whatsapp to " +
					updatedClaimant.insuredContactDetails,
			);
			setIsSubmitting(false);
		} catch (e) {
			console.error(e);
			alert("Failed to update claimant.");
		}
	};

	const handleChange = (e) => {
		setAssignedAdjuster(e.target.value);
	};

	const handleExpandClick = async (assignment) => {
		try {
			dispatch(setAssignment(assignment));
			const response = await fetchAttachmentList(
				assignment.assignmentName,
			);

			const pdfFiles =
				response?.length > 0
					? response
							.filter((data) => data.fileName.endsWith(".pdf"))
							.map((data) => data.fileName)
					: [];

			const pngFiles =
				response?.length > 0
					? response
							.filter((data) => data.fileName.endsWith(".png"))
							.map((data) => ({
								imageData: `${process.env.REACT_APP_API_URL}/GetAttachment?assignmentName=${assignment.assignmentName}&attachmentName=${data.fileName}`,
								height: data.height,
								width: data.width,
								description: data.description,
								customDescription: data.customDescription,
								isSaved: true,
							}))
					: [];

			// Update attachments
			setAttachments((prev) => ({
				...prev,
				[assignment.assignmentName]: pdfFiles,
			}));

			// Update snapshots
			setSnapshots((prev) => ({
				...prev,
				[assignment.assignmentName]: pngFiles,
			}));

			// Initialize addedSnapshots if not already present
			setAddedSnapshots((prev) => ({
				...prev,
				[assignment.assignmentName]: pngFiles,
			}));

			setExpanded((prevExpanded) =>
				prevExpanded === assignment.id ? null : assignment.id,
			);
		} catch (error) {
			console.error("Error in handleExpandClick:", error);
		}
	};

	const handleNotifyClick = () => {
		if (currentAssignment?.claimants?.length > 0) {
			setSelectedClaimantIdx(0);
			setCurrentContact(
				currentAssignment.claimants[0].insuredContactDetails || "",
			);
		}
		setDialogOpen(true);
	};

	const handleContactChange = (e) => {
		setCurrentContact(e.target.value);
	};

	const handleAssignAdjusterClick = () => {
		setAdjusterDialogOpen(true);
	};

	const handleCreateAssignment = () => {
		navigate("/claim/New");
	};

	const onUploadFromGalleryClicked = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		} else {
			console.warn("fileInputRef is not yet attached");
		}
	};

	const processFiles = async (selectedFiles) => {
		const snapshotData = await Promise.all(
			Array.from(selectedFiles).map(async (file) => {
				const base64Data = await encodeImageToBase64(file); // Your function
				const fullBase64 = `data:${file.type};base64,${base64Data}`; // Reconstruct full data URL
				const analysis = await analyseImage(fullBase64);
				return {
					imageData: fullBase64,
					...analysis,
					isSaved: false, // Add this property for newly added snapshots
				};
			}),
		);
		return snapshotData;
	};

	const handleFileChange = async (event) => {
		const selectedFiles = Array.from(event.target.files);
		setIsSubmitting(true);
		const snapshotData = await processFiles(selectedFiles);

		setIsSubmitting(false);

		// Safely handle the update of snapshots
		setSnapshots((prevSnapshots) => {
			const currentSnapshots =
				prevSnapshots[currentAssignment.assignmentName] || [];
			const updatedSnapshots = [...currentSnapshots, ...snapshotData]; // Use spread operator to merge arrays

			// Initialize editedDescriptions for new snapshots
			setEditedDescriptions((prevEditedDescriptions) => {
				const updatedEditedDescriptions = { ...prevEditedDescriptions };
				return updatedEditedDescriptions;
			});

			return {
				...prevSnapshots,
				[currentAssignment.assignmentName]: updatedSnapshots,
			};
		});
	};
	const handleDescriptionChange = (assignmentName, index, value) => {
		const key = `${assignmentName}-${index}`;
		setEditedDescriptions((prev) => {
			const updatedDescriptions = { ...prev };
			if (value === snapshots[assignmentName][index].description) {
				delete updatedDescriptions[key]; // Remove if no actual change
			} else {
				updatedDescriptions[key] = value; // Store edited description
			}
			return updatedDescriptions;
		});
	};

	const handleCustomDescriptionChange = (assignmentName, index, value) => {
		const key = `${assignmentName}-${index}`;
		setEditedCustomDescriptions((prev) => {
			const updatedCustomDescriptions = { ...prev };
			if (
				value ===
				(snapshots[assignmentName][index].customDescription || "")
			) {
				delete updatedCustomDescriptions[key]; // Remove if no actual change
			} else {
				updatedCustomDescriptions[key] = value; // Store edited custom description
			}
			return updatedCustomDescriptions;
		});
	};

	const handleHeightChange = (assignmentName, index, value) => {
		const key = `${assignmentName}-${index}`;
		setEditedHeights((prev) => {
			const updatedHeights = { ...prev };
			const numValue = value === "" ? 0 : Number(value);
			if (numValue === snapshots[assignmentName][index].height) {
				delete updatedHeights[key]; // Remove if no actual change
			} else {
				updatedHeights[key] = numValue; // Store edited height
			}
			return updatedHeights;
		});
	};

	const handleWidthChange = (assignmentName, index, value) => {
		const key = `${assignmentName}-${index}`;
		setEditedWidths((prev) => {
			const updatedWidths = { ...prev };
			const numValue = value === "" ? 0 : Number(value);
			if (numValue === snapshots[assignmentName][index].width) {
				delete updatedWidths[key]; // Remove if no actual change
			} else {
				updatedWidths[key] = numValue; // Store edited width
			}
			return updatedWidths;
		});
	};

	// When dropdown changes, update both selectedClaimantIdx and currentContact
	const handleClaimantSelect = (e) => {
		const idx = Number(e.target.value);
		setSelectedClaimantIdx(idx);
		setCurrentContact(
			currentAssignment.claimants[idx].insuredContactDetails || "",
		);
	};

	const renderActions = (assignment) => {
		const { status } = assignment;
		const { role } = user;
		const snapshotsExist = snapshots[assignment.assignmentName]?.length > 0;

		const actions = [];

		const renderButton = (onClick, color, text, additionalProps = {}) => (
			<Button
				variant="contained"
				size="small"
				color={color}
				onClick={onClick}
				disabled={isSubmitting}
				startIcon={isSubmitting && <CircularProgress size={20} />}
				{...additionalProps}
			>
				{text}
			</Button>
		);

		if (status === "Completed" && role !== "Maker" && role !== "SysAdmin")
			return null;

		const showEditButton =
			(role === "Maker" || role === "SysAdmin") &&
			(status !== "Approved" || status !== "Completed");

		if (showEditButton) {
			actions.push(
				renderButton(onEditClick, "secondary", "Edit", {
					sx: { marginRight: 2 },
				}),
			);
		}

		if (status.toUpperCase() === "NEW" && role === "Manager") {
			actions.push(
				renderButton(
					handleAssignAdjusterClick,
					"primary",
					"Assign Adjuster",
					{ sx: { marginRight: 2 } },
				),
			);
		}

		if (
			(status === "Assigned" || status === "InProgress") &&
			!snapshotsExist &&
			role === "Adjuster"
		) {
			if (!joined) {
				actions.push(
					renderButton(
						() =>
							handleNotifyClick(
								currentAssignment.insuredContactDetails,
							),
						"primary",
						"Notify Claimant",
						{ sx: { marginRight: 2 } },
					),
				);
			}
		}

		if (
			status === "InProgress" &&
			(role === "Manager" || role === "Adjuster")
		) {
			if (!joined) {
				actions.push(
					renderButton(
						() => setJoined(true),
						"primary",
						"Join Call",
						{
							sx: { marginRight: 2 },
						},
					),
				);
			}
			if (!joined && snapshotsExist) {
				actions.push(
					renderButton(
						() => openNewTab(assignment),
						"secondary",
						isSubmitting ? "Submitting..." : "View Loss Assessment",
						{ sx: { marginRight: 2 } },
					),
				);
			}
			if (!joined) {
				actions.push(
					<input
						type="file"
						ref={fileInputRef}
						style={{ display: "none" }}
						onChange={handleFileChange}
						multiple
						accept="image/*"
					/>,
				);
				actions.push(
					<Button
						onClick={onUploadFromGalleryClicked}
						variant="outlined"
						color="secondary"
						size="small"
						disabled={isSubmitting}
						startIcon={
							isSubmitting && <CircularProgress size={20} />
						}
					>
						{isSubmitting ? "Submitting..." : "Upload Images"}
					</Button>,
				);
				if (!joined && snapshotsExist) {
					const hasUnsavedSnapshots =
						Array.isArray(
							snapshots?.[currentAssignment.assignmentName],
						) &&
						snapshots[currentAssignment.assignmentName]?.length >
							0 &&
						snapshots[currentAssignment.assignmentName].some(
							(snapshot) => snapshot.isSaved === false,
						);

					if (hasUnsavedSnapshots) {
						actions.push(
							renderButton(
								() => handleSnapshotsSaving(),
								"success",
								"Save Snapshots",
								{ sx: { marginRight: 2, marginLeft: 2 } },
							),
						);
					}
				}
			}
		}

		if (status === "Pending Verification") {
			if (snapshotsExist) {
				actions.push(
					renderButton(
						() => openNewTab(assignment),
						"primary",
						isSubmitting ? "Submitting..." : "View Loss Assessment",
						{ sx: { marginRight: 2 } },
					),
				);
			}
		}

		if (status === "Review" && user?.role === "Adjuster") {
			// Skip acceptance form flow entirely if no templateId (e.g. AIA assignments)
			const hasTemplateId = !!currentAssignment.templateId;
			const attachment = attachments[currentAssignment.assignmentName];
			const hasAcceptanceForm =
				!attachment || !Array.isArray(attachment)
					? false
					: attachments[currentAssignment.assignmentName].some(
							(file) =>
								file === "Form_of_Acceptance(R).pdf" ||
								file === "Form_of_Acceptance(P).pdf" ||
								file ===
									`${currentAssignment.assignmentName}_signed_acceptance_form.pdf`,
						);
			return (
				<CardActions disableSpacing>
					{hasTemplateId &&
						!hasAcceptanceForm &&
						renderButton(
							() => openAcceptanceForm(assignment),
							"primary",
							"View Acceptance Form",
							{ sx: { marginRight: 2 } },
						)}
				</CardActions>
			);
		}

		if (status === "Review" && user?.role === "Manager") {
			const hasTemplateId = !!currentAssignment.templateId;
			const attachment = attachments[currentAssignment.assignmentName];
			const hasAcceptanceForm =
				!attachment || !Array.isArray(attachment)
					? false
					: attachments[currentAssignment.assignmentName].some(
							(file) =>
								file === "Form_of_Acceptance(R).pdf" ||
								file === "Form_of_Acceptance(P).pdf" ||
								file ===
									`${currentAssignment.assignmentName}_signed_acceptance_form.pdf`,
						);
			actions.push(
				renderButton(
					() => openNewTab(assignment),
					"primary",
					"View Loss Assessment",
					{ sx: { marginLeft: 0 } },
				),
			);
			// Allow approval without acceptance form when no templateId (e.g. AIA assignments)
			const canApprove =
				currentAssignment.settledAmount !== 0 &&
				(!hasTemplateId || hasAcceptanceForm);
			if (canApprove) {
				actions.push(
					renderButton(onAssignmentApproved, "success", "Approve", {
						sx: { marginLeft: 2 },
					}),
				);
			}
		}

		if (status === "Approved" && user?.role === "Manager") {
			actions.push(
				renderButton(onAssignmentRevoke, "warning", "Revoke", {
					sx: { marginLeft: 0 },
				}),
			);
		}

		if (user?.role === "Maker" && status === "Approved") {
			actions.push(
				renderButton(
					() => setIsShowInvoice(true),
					"secondary",
					"Generate Invoice",
					{ sx: { marginLeft: 2 } },
				),
			);
		}

		return actions.length ? (
			<CardActions disableSpacing>{actions}</CardActions>
		) : null;
	};

	const handleMenuItemClick = (item) => {
		switch (item) {
			case "My Assignments":
			case "All Assignments":
				navigate("/home");
				break;
			case "New Assignment":
				navigate("/claim/New");
				break;
			case "Data Reports":
				navigate("/data-reports");
				break;
			case "Create User":
				navigate("/createUser");
				break;
			case "Change Password":
				navigate("/changePassword");
				break;
			case "Profile":
				navigate("/profile");
				break;
			case "Logout":
				handleLogout();
				break;
			default:
				break;
		}
	};

	const handleLogout = () => {
		dispatch(setUser(null));
		dispatch(setAssignment(null));
		navigate("/");
	};

	const handleCallEnd = (snapshotsData) => {
		setJoined(false);
		setSnapshots((prevSnapshots) => {
			const currentSnapshots =
				prevSnapshots[currentAssignment.assignmentName] || [];
			return {
				...prevSnapshots,
				[currentAssignment.assignmentName]: [
					...currentSnapshots,
					...snapshotsData,
				], // Use spread operator to merge arrays
			};
		});
	};

	const fetchBlob = async (blobUrl) => {
		try {
			const response = await fetch(blobUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch blob: ${response.statusText}`);
			}
			const blob = await response.blob(); // Convert response to a Blob

			return blob;
		} catch (error) {
			console.error("Error fetching blob:", error);
		}
	};

	const onCloseInvoice = (updatedAssignmentList) => {
		setAssignments(updatedAssignmentList);
		setIsShowInvoice(false);
	};

	const handleCompanyFilterChange = (event) => {
		const { name, checked } = event.target;
		if (name === "Select All") {
			setFilterCompany((prev) => {
				const next = Object.keys(prev).reduce((acc, key) => {
					acc[key] = checked;
					return acc;
				}, {});
				return next;
			});
			return;
		}

		setFilterCompany((prev) => {
			const next = { ...prev, [name]: checked };
			if (!checked) {
				next["Select All"] = false;
			} else {
				const allChecked = Object.keys(next)
					.filter((k) => k !== "Select All")
					.every((k) => next[k]);
				if (allChecked) next["Select All"] = true;
			}
			return next;
		});
	};

	const handleFilterChange = (event) => {
		const { name, checked } = event.target;

		if (name === "Select All") {
			// Toggle all filter statuses based on "Select All" checkbox
			const updatedStatus = Object.keys(filterStatus).reduce(
				(acc, key) => {
					acc[key] = checked;
					return acc;
				},
				{},
			);
			setFilterStatus(updatedStatus);
		} else {
			// Toggle individual filter status
			setFilterStatus((prevStatus) => {
				const updatedStatus = { ...prevStatus, [name]: checked };

				// If any individual filter is unchecked, uncheck "Select All"
				if (!checked) {
					updatedStatus["Select All"] = false;
				} else {
					// If all individual filters are checked, check "Select All"
					const allChecked = Object.keys(updatedStatus)
						.filter((key) => key !== "Select All")
						.every((key) => updatedStatus[key]);
					if (allChecked) {
						updatedStatus["Select All"] = true;
					}
				}

				return updatedStatus;
			});
		}
	};

	const filteredListData = assignments.filter((item) => {
		const isAnyFilterSelected = Object.values(filterStatus).some(
			(value) => value,
		);

		if (!isAnyFilterSelected) {
			return false; // No filter selected, show all items
		}

		// Company filter: if any company checkbox (excluding Select All) is enabled, enforce it
		const companyKeys = Object.keys(filterCompany).filter(
			(k) => k !== "Select All",
		);
		const anyCompanySelected = companyKeys.some((k) => filterCompany[k]);
		const companyPass = !anyCompanySelected
			? true
			: !!filterCompany[item.insuranceCompany];

		return (
			(item.assignmentName
				.toLowerCase()
				.includes(searchQuery.toLowerCase()) ||
				item.claimNumber
					.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				item.policyNumber
					.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				item.insuredName
					.toLowerCase()
					.includes(searchQuery.toLowerCase())) &&
			filterStatus[item.status] &&
			companyPass
		);
	});

	const handleSnapshotsSaving = async () => {
		try {
			if (!currentAssignment?.assignmentName) {
				alert("No assignment selected. Please select an assignment.");
				return;
			}

			const assignmentSnapshots =
				snapshots?.[currentAssignment.assignmentName];
			const activeSnapshots = Array.isArray(assignmentSnapshots)
				? assignmentSnapshots?.filter((snap) => snap.isSaved === false)
				: [];

			if (activeSnapshots.length === 0) {
				alert(
					"No snapshots available to save. Please upload snapshots first.",
				);
			}

			let request = new FormData();
			setIsSubmitting(true);

			request.append("assignmentName", currentAssignment.assignmentName);

			const attachmentDetails = [];
			const d = new Date();
			let time = d.getTime();

			// Process snapshots
			for (let index = 0; index < activeSnapshots.length; index++) {
				const snapshot = activeSnapshots[index];
				const fileName = `SnapShot-${
					currentAssignment.assignmentName
				}-${time}-${index + 1}.png`;

				// Convert image only if necessary
				const imageBlob = snapshot.imageData.startsWith(
					"data:image/png",
				)
					? base64ToBlob(snapshot.imageData, "image/png")
					: await convertToPng(snapshot.imageData);

				request.append(`images${index}`, imageBlob, fileName);

				attachmentDetails.push({
					fileName: fileName,
					height: isNaN(snapshot.height) ? 0 : snapshot.height,
					width: isNaN(snapshot.width) ? 0 : snapshot.width,
					description: snapshot.description || "",
					customDescription: snapshot.customDescription || "",
				});
			}

			// Append attachment details
			request.append(
				"AttachmentDetails",
				JSON.stringify(attachmentDetails),
			);

			// Upload snapshots
			await uploadAttachment(request);

			// Update snapshots state
			setSnapshots((prevSnapshots) => {
				const updatedSnapshots = { ...prevSnapshots };

				if (!updatedSnapshots[currentAssignment.assignmentName]) {
					return prevSnapshots;
				}

				updatedSnapshots[currentAssignment.assignmentName] =
					updatedSnapshots[currentAssignment.assignmentName].map(
						(snap) => ({
							...snap,
							isSaved: true, // Mark as saved
						}),
					);

				return updatedSnapshots;
			});

			alert("Snapshots saved successfully.");
		} catch (error) {
			alert(`Error saving snapshots: ${error.message}`);
			console.error("Snapshot saving error:", error);
		} finally {
			setIsSubmitting(false);
		}
	};
	const handleLossSubmit = async (request, liveCallSnapshots = []) => {
		try {
			if (user?.role === "Adjuster") {
				request.append("status", "Review");
			} else {
				request.append("status", currentAssignment.status);
			}

			// First, determine which snapshots to use
			const assignmentName = currentAssignment?.assignmentName;
			if (!assignmentName) {
				throw new Error("No assignment name available");
			}

			// Get snapshots either from passed data or current state
			const assignmentSnapshots =
				snapshots?.[currentAssignment.assignmentName];
			const activeSnapshots = getUnsavedSnapshots(
				assignmentSnapshots,
				liveCallSnapshots,
			);

			const attachmentDetails = [];
			const d = new Date();
			let time = d.getTime();

			// Process snapshots
			for (let index = 0; index < activeSnapshots.length; index++) {
				const snapshot = activeSnapshots[index];
				if (!snapshot) continue; // Skip if snapshot is undefined

				const fileName = `SnapShot-${assignmentName}-${time}-${
					index + 1
				}.png`;

				// Handle both blob URLs and base64 data
				const imageBlob = snapshot.imageData.startsWith("blob:")
					? await fetchBlob(snapshot.imageData)
					: base64ToBlob(snapshot.imageData, "image/png");

				if (!imageBlob) {
					console.error(`Failed to process image at index ${index}`);
					continue;
				}

				request.append(`images${index}`, imageBlob, fileName);

				attachmentDetails.push({
					fileName: fileName,
					height:
						snapshot.height === " " || isNaN(snapshot.height)
							? 0
							: snapshot.height,
					width:
						snapshot.width === " " || isNaN(snapshot.width)
							? 0
							: snapshot.width,
					description: snapshot.description || "",
					customDescription: snapshot.customDescription || "",
				});
			}

			if (attachmentDetails.length === 0) {
				console.log("No snapshots to upload.");
			}

			// Append attachment details and company name
			request.append(
				"attachmentDetails",
				JSON.stringify(attachmentDetails),
			);
			request.append(
				"companyName",
				currentAssignment.insuranceCompany || "",
			);

			// Upload snapshots and update assignments
			const assignmentUpdated = await uploadSnapshots(request, user);
			if (assignmentUpdated === 1) {
				const assignments = await fetchAssignments(user);

				setAssignments(assignments);

				// Update snapshots state to mark all as saved
				setSnapshots((prev) => ({
					...prev,
					[assignmentName]: (prev[assignmentName] || []).map(
						(snap) => ({
							...snap,
							isSaved: true,
						}),
					),
				}));

				alert("Loss assessment submitted successfully.");

				// Reset docIdRef after successful submission
				docIdRef.current = null;

				// Create a Map once when you get the updatedAssignmentList
				const assignmentMap = new Map(
					assignments.map((assignment) => [
						assignment.assignmentName,
						assignment,
					]),
				);

				// Then use O(1) lookup
				const updated_current_assignment = await assignmentMap.get(
					currentAssignment.assignmentName,
				);

				// Keep Redux store in sync so AcceptanceForm sees fresh data (e.g. settledAmount)
				if (updated_current_assignment) {
					dispatch(setAssignment(updated_current_assignment));
				}

				// After successful submission, and updated assignment, create acceptance form document if template ID exists
				await createAcceptanceFormDocument(updated_current_assignment);
			}
		} catch (error) {
			console.error("handleLossSubmit error:", error);
			alert(
				`Error: ${error.message || "Failed to submit loss assessment"}`,
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Function to create acceptance form document
	const createAcceptanceFormDocument = async (assignment) => {
		try {
			// Only proceed if assignment has a templateId and user is an adjuster
			if (!assignment.templateId || !user || user.role !== "Adjuster") {
				console.log(
					"Not creating document: missing templateId or not an adjuster",
				);
				return;
			}

			setIsLoading(true);

			// Prevent multiple document creations
			if (docIdRef.current) {
				console.log("Document already being created or exists");
				return;
			}

			docIdRef.current = "creating";
			const documentName = `Acceptance Form - ${assignment.assignmentName}`;

			// Helper function to map nature of loss codes to descriptions
			const getNatureOfLossDescription = (code) => {
				switch (code) {
					case "FL":
						return "Flood";
					case "WS":
						return "Windstorm";
					case "FLWS":
						return "Flood & Windstorm";
					case "OT":
						return "Other";
					default:
						return code;
				}
			};

			// Create metadata with all fields needed for the form
			const documentMetadata = {
				assignmentName: assignment.assignmentName,
				insuranceCompany: assignment.insuranceCompany,
				claimants: assignment.claimants || [],
				adjuster: user,
				// Add fields required for document placeholders
				address: assignment.address,
				policyNumber: assignment.policyNumber,
				claimNumber: assignment.claimNumber,
				natureOfLoss: getNatureOfLossDescription(
					assignment.natureOfLoss,
				),
				dateOfLoss: assignment.dateOfLoss,
				situationOfLoss: assignment.situationOfLoss,
				// Handle fields that might have different names or need transformation
				settledAmount:
					assignment.settledAmount || assignment.claimAmount,
				settledAmountInWords: assignment.settledAmount
					? `${numberToWords(assignment.settledAmount)}`
					: `${numberToWords(assignment.claimAmount || 0)}`,
			};

			// // Log metadata for debugging
			// console.log('Document metadata for form fields:', documentMetadata);

			// Dispatch create document action
			const resultAction = await dispatch(
				createDocumentAsync({
					templateId: assignment.templateId,
					documentName,
					metadata: documentMetadata,
				}),
			);

			if (createDocumentAsync.fulfilled.match(resultAction)) {
				const docId = resultAction.payload.documentId;
				docIdRef.current = docId;

				// Update assignment with new document ID
				dispatch(
					updateAssignmentAsync({
						assignmentId: assignment.id,
						documentId: docId,
					}),
				);

				// Import the document finalization function
				const { ensureDocumentIsFinalized } =
					await import("./utility/opensign-helpers");

				// Ensure the document is finalized
				await ensureDocumentIsFinalized(docId);

				// Reload assignments to get the updated one with document ID
				const updatedAssignmentList = await fetchAssignments(user);
				if (updatedAssignmentList) {
					setAssignments(updatedAssignmentList);
				}
			}
		} catch (error) {
			console.error("Failed to create acceptance form document:", error);
			alert(`Error creating document: ${error.message}`);
			docIdRef.current = null;
		} finally {
			setIsLoading(false);
		}
	};

	const openNewTab = (assignment) => {
		if (!assignment || !assignment.assignmentName) {
			console.error(
				"Error: Assignment is undefined or missing assignmentName.",
			);
			return;
		}

		const newTab = window.open(
			`/lossform/${assignment.assignmentName}`,
			"_blank",
		);

		if (!newTab) {
			console.error("Failed to open new tab. Please allow pop-ups.");
			return;
		}

		const handleMessage = (event) => {
			if (event.origin !== window.location.origin) {
				return;
			}

			const receivedData = event.data;

			if (
				!receivedData ||
				typeof receivedData !== "object" ||
				!receivedData.message
			) {
				console.error("Invalid data received:", receivedData);
				return;
			}

			if (typeof receivedData.message !== "object") {
				console.error(
					"Expected an object in message field:",
					receivedData.message,
				);
				return;
			}

			const formData = new FormData();
			Object.entries(receivedData.message).forEach(([key, value]) => {
				formData.append(key, value);
			});

			handleLossSubmit(formData);

			// Clean up the event listener after processing
			window.removeEventListener("message", handleMessage);
		};

		window.addEventListener("message", handleMessage);

		// Ensure the event listener is removed if the tab is closed without sending a message
		const checkTabClosed = setInterval(() => {
			if (newTab.closed) {
				clearInterval(checkTabClosed);
				window.removeEventListener("message", handleMessage);
			}
		}, 1000);
	};

	const onEditClick = () => {
		navigate("/claim/Edit");
	};

	const openAcceptanceForm = (assignment) => {
		// Ensure Redux store has the latest assignment data (including settledAmount)
		// before navigating, so AcceptanceForm doesn't use stale data.
		dispatch(setAssignment(assignment));
		navigate(`/acceptance/${assignment.assignmentName}`, {
			state: { isAdjustor: true },
		});
	};

	const onAssignmentApproved = async () => {
		const updatedAssignmentList = await updateAssignmentStatus(
			currentAssignment.assignmentName,
			"Approved",
			user,
		);
		setAssignments(updatedAssignmentList);
		alert("Assignment Approved.");
	};

	const onAssignmentRevoke = async () => {
		const updatedAssignmentList = await updateAssignmentStatus(
			currentAssignment.assignmentName,
			"Review",
			user,
		);
		setAssignments(updatedAssignmentList);
		alert("Assignment Revoked.");
	};

	const handleSnapshotDeletion = (assignmentName, index) => {
		setSnapshots((prevSnapshots) => {
			// Create a deep copy to avoid reference issues
			const updatedSnapshots = { ...prevSnapshots };

			if (!Array.isArray(updatedSnapshots[assignmentName])) {
				console.warn("No snapshots found for the given assignment.");
				return prevSnapshots;
			}

			// Remove the snapshot at the specified index
			updatedSnapshots[assignmentName] = updatedSnapshots[
				assignmentName
			].filter((_, i) => i !== index);

			// If no snapshots remain, set the assignment key to an empty array
			if (updatedSnapshots[assignmentName].length === 0) {
				updatedSnapshots[assignmentName] = [];
			}

			return updatedSnapshots;
		});

		// Also update addedSnapshots to maintain consistency
		setAddedSnapshots((prevAddedSnapshots) => {
			const updatedAddedSnapshots = { ...prevAddedSnapshots };

			if (!Array.isArray(updatedAddedSnapshots[assignmentName])) {
				console.warn(
					"No added snapshots found for the given assignment.",
				);
				return prevAddedSnapshots;
			}

			updatedAddedSnapshots[assignmentName] = updatedAddedSnapshots[
				assignmentName
			].filter((_, i) => i !== index);

			// If no added snapshots remain, set the assignment key to an empty array
			if (updatedAddedSnapshots[assignmentName].length === 0) {
				updatedAddedSnapshots[assignmentName] = [];
			}

			return updatedAddedSnapshots;
		});
	};
	const handleGeneration = async (assignment, index, image_url) => {
		if (
			!assignment?.assignmentName ||
			!snapshots[assignment.assignmentName]
		) {
			console.error("Invalid assignment or missing snapshots");
			alert("Cannot analyze image: Invalid assignment data");
			return;
		}

		const snapshot = snapshots[assignment.assignmentName][index];
		if (!snapshot?.imageData) {
			console.error("Invalid snapshot or missing image data");
			alert("Cannot analyze image: Missing image data");
			return;
		}

		try {
			setIsSubmitting(true);

			const analyzedSnapshot = await analyzeSnapshotIfNeeded(snapshot);

			if (analyzedSnapshot.error) {
				console.error(
					"Error analyzing image:",
					analyzedSnapshot.error,
					analyzedSnapshot.details,
				);
				alert(
					`Failed to analyze image: ${analyzedSnapshot.details || analyzedSnapshot.error}\n\nPlease try again.`,
				);
				return;
			}

			const key = `${assignment.assignmentName}-${index}`;
			const updates = {
				customDescription: analyzedSnapshot.customDescription,
				height: parseFloat(analyzedSnapshot.height) || 0,
				width: parseFloat(analyzedSnapshot.width) || 0,
				description: analyzedSnapshot.description,
			};

			// Batch update all states together
			setSnapshots((prevSnapshots) => {
				const updatedSnapshots = { ...prevSnapshots };
				if (!updatedSnapshots[assignment.assignmentName]) {
					updatedSnapshots[assignment.assignmentName] = [];
				}

				updatedSnapshots[assignment.assignmentName] = updatedSnapshots[
					assignment.assignmentName
				].map((snap, i) =>
					i === index ? { ...snap, ...updates } : snap,
				);

				return updatedSnapshots;
			});

			// Update all edited fields states in one go using a helper function
			const updateEditedState = (setter, value) => {
				setter((prev) => ({ ...prev, [key]: value }));
			};

			updateEditedState(
				setEditedCustomDescriptions,
				updates.customDescription,
			);
			updateEditedState(setEditedHeights, updates.height);
			updateEditedState(setEditedWidths, updates.width);
			updateEditedState(setEditedDescriptions, updates.description);

			alert(
				"Image analyzed successfully! Click 'Save All Changes' to confirm the updates.",
			);
		} catch (error) {
			console.error("Error in handleGeneration:", error);
			alert("Error analyzing image. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleImageClick = (image) => {
		setSelectedImage(image);
		setImageDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setImageDialogOpen(false);
	};

	const handleDownload = (fileName, assignmentName) => {
		const fileUrl = `${process.env.REACT_APP_API_URL}/GetAttachment?assignmentName=${assignmentName}&attachmentName=${fileName}`; // Replace with your file URL
		// Open the file URL in a new browser tab
		window.open(fileUrl, "_blank", "noopener, noreferrer");
	};
	const snapshotNeedsAnalysis = (snapshot) => {
		// If any field is empty, undefined, null, or zero (for numerical values)
		return (
			!snapshot.customDescription?.trim() ||
			!snapshot.height ||
			snapshot.height === 0 ||
			isNaN(snapshot.height) ||
			!snapshot.width ||
			snapshot.width === 0 ||
			isNaN(snapshot.width) ||
			!snapshot.description?.trim() ||
			snapshot.description === "No description available"
		);
	};

	return (
		<Box
			sx={{
				width: "100%",
				margin: "auto",
			}}
		>
			<AppBar position="static">
				<Toolbar>
					<IconButton
						edge="start"
						color="inherit"
						aria-label="menu"
						onClick={toggleDrawer(setDrawerOpen)(true)}
					>
						<MenuIcon />
					</IconButton>
					<Box
						component="img"
						src={logo}
						alt="Logo"
						sx={{ height: 40, mr: 2, borderRadius: "50%" }}
					/>
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						{user?.role === "SysAdmin"
							? "All Assignments"
							: "Assignment List"}
					</Typography>
					<IconButton edge="end" color="inherit">
						<Avatar>
							<AccountCircle />
						</Avatar>
					</IconButton>
				</Toolbar>
			</AppBar>
			<Drawer
				anchor="left"
				open={drawerOpen}
				onClose={toggleDrawer(setDrawerOpen)(false)}
			>
				{sideMenuList(
					toggleDrawer(setDrawerOpen),
					handleMenuItemClick,
					user?.role,
				)}
			</Drawer>

			<Container
				maxWidth={true}
				sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 4 } }}
			>
				<Grid2 container spacing={{ xs: 1, md: 3 }}>
					{/* Filters Column - hidden on mobile, shown in a drawer instead */}
					{!isMobile && (
						<Grid2 item size={{ xs: 12, md: 3 }}>
							<FilterPanel
								filterStatus={filterStatus}
								onFilterChange={handleFilterChange}
								filterCompany={filterCompany}
								onCompanyFilterChange={
									handleCompanyFilterChange
								}
							/>
						</Grid2>
					)}

					{/* Mobile filter drawer */}
					{isMobile && (
						<Drawer
							anchor="left"
							open={filterDrawerOpen}
							onClose={() => setFilterDrawerOpen(false)}
							PaperProps={{ sx: { width: 280, p: 1 } }}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									p: 1,
								}}
							>
								<Typography variant="h6">Filters</Typography>
								<IconButton
									onClick={() => setFilterDrawerOpen(false)}
								>
									<CloseIcon />
								</IconButton>
							</Box>
							<FilterPanel
								filterStatus={filterStatus}
								onFilterChange={handleFilterChange}
								filterCompany={filterCompany}
								onCompanyFilterChange={
									handleCompanyFilterChange
								}
							/>
						</Drawer>
					)}

					{/* Main Content Column */}
					<Grid2 item size={{ xs: 12, md: 9 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 2,
							}}
						>
							{isMobile && (
								<IconButton
									onClick={() => setFilterDrawerOpen(true)}
									color="primary"
									aria-label="open filters"
								>
									<FilterListIcon />
								</IconButton>
							)}
							<Paper
								component="form"
								sx={{
									p: "2px 4px",
									display: "flex",
									alignItems: "center",
									flex: 1,
								}}
							>
								<InputBase
									sx={{
										ml: 1,
										flex: 1,
										color: "string.main",
										fontWeight: "medium",
									}}
									placeholder="Search Assignments..."
									onChange={handleSearchChange}
									value={searchQuery}
									inputProps={{
										"aria-label": "search assignments",
									}}
								/>
								<IconButton
									type="button"
									sx={{ p: "10px" }}
									aria-label="search"
								>
									<SearchIcon />
								</IconButton>
							</Paper>
						</Box>

						<List>
							{filteredListData.map((assignment) => (
								<ListItem
									key={assignment.id}
									sx={{
										display: "block",
										mb: 1,
										px: { xs: 0, md: 2 },
									}}
								>
									<Card sx={{ backgroundColor: "#F8F8FF" }}>
										<CardHeader
											title={`${assignment.assignmentName} | ${assignment.claimants[0].insuredName} | ${assignment.insuranceCompany}`}
											subheader={assignment.status}
											action={
												<IconButton
													onClick={() =>
														handleExpandClick(
															assignment,
														)
													}
													aria-expanded={
														expanded ===
														assignment.id
													}
													aria-label="show more"
												>
													<ExpandMoreIcon
														sx={{
															transform:
																expanded ===
																assignment.id
																	? "rotate(180deg)"
																	: "rotate(0deg)",
															transition:
																"transform 1s",
														}}
													/>
												</IconButton>
											}
											slotProps={{
												title: {
													variant: isMobile
														? "body2"
														: "h6",
													sx: {
														wordBreak: "break-word",
														overflowWrap:
															"break-word",
													},
												},
											}}
											sx={{
												ml: { xs: 0, md: 1 },
												flex: 1,
												color: "string.main",
												fontWeight: "medium",
												p: { xs: 1, md: 2 },
											}}
										/>
										<Collapse
											in={expanded === assignment.id}
											timeout="auto"
											unmountOnExit
										>
											<CardContent>
												<Box sx={{ p: 1 }}>
													{renderActions(assignment)}
													<Divider sx={{ my: 0.5 }} />
													<AssignmentDetailsSection
														assignment={assignment}
													/>
												</Box>
												<Divider
													style={{ marginBottom: 10 }}
												/>
												<Typography
													variant="h6"
													sx={{
														mb: 2,
														color: "primary.main",
														fontWeight: "medium",
													}}
												>
													Attachments:
												</Typography>
												{Array.isArray(
													attachments[
														assignment
															.assignmentName
													],
												) &&
												attachments[
													assignment.assignmentName
												].filter((attachment) =>
													attachment.endsWith(".pdf"),
												).length > 0 ? (
													attachments[
														assignment
															.assignmentName
													]
														.filter((attachment) =>
															attachment.endsWith(
																".pdf",
															),
														) // Filter attachments with .pdf only
														.map(
															(
																attachment,
																index,
															) => (
																<Chip
																	label={
																		attachment
																	}
																	variant="outlined"
																	color="info"
																	icon={
																		<FileDownloadIcon />
																	}
																	onClick={() =>
																		handleDownload(
																			attachment,
																			assignment.assignmentName,
																		)
																	}
																	style={{
																		marginBottom: 10,
																		marginRight: 10,
																	}}
																/>
															),
														)
												) : (
													<Typography>
														No attachments
														available.
													</Typography>
												)}
												<Divider
													style={{
														marginBottom: 10,
														marginTop: 10,
													}}
												/>
												{snapshots[
													assignment.assignmentName
												] &&
													snapshots[
														assignment
															.assignmentName
													].length > 0 && (
														<div>
															<Typography
																variant="h6"
																sx={{
																	mb: 2,
																	color: "primary.main",
																	fontWeight:
																		"medium",
																}}
															>
																Captured
																Snapshots:
															</Typography>
															<Stack
																direction="column"
																spacing={4}
																sx={{
																	width: "100%",
																}}
															>
																{snapshots[
																	assignment
																		.assignmentName
																].map(
																	(
																		snapshot,
																		index,
																	) => (
																		<SnapshotCard
																			key={
																				index
																			}
																			snapshot={
																				snapshot
																			}
																			index={
																				index
																			}
																			assignment={
																				assignment
																			}
																			isSubmitting={
																				isSubmitting
																			}
																			addedSnapshots={
																				addedSnapshots
																			}
																			editedDescriptions={
																				editedDescriptions
																			}
																			editedCustomDescriptions={
																				editedCustomDescriptions
																			}
																			editedHeights={
																				editedHeights
																			}
																			editedWidths={
																				editedWidths
																			}
																			snapshotNeedsAnalysis={
																				snapshotNeedsAnalysis
																			}
																			onImageClick={
																				handleImageClick
																			}
																			onGeneration={
																				handleGeneration
																			}
																			onSaveDescription={
																				saveDescription
																			}
																			onSnapshotDeletion={
																				handleSnapshotDeletion
																			}
																			onCustomDescriptionChange={
																				handleCustomDescriptionChange
																			}
																			onHeightChange={
																				handleHeightChange
																			}
																			onWidthChange={
																				handleWidthChange
																			}
																			onDescriptionChange={
																				handleDescriptionChange
																			}
																		/>
																	),
																)}
															</Stack>
														</div>
													)}
											</CardContent>
										</Collapse>
									</Card>
								</ListItem>
							))}
						</List>
					</Grid2>
				</Grid2>
			</Container>

			{/* Video Call Dialog - portrait with floating self-view */}
			<Dialog
				open={joined}
				onClose={() => {}}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: { xs: "100vw", sm: "min(440px, 95vw)" },
						height: { xs: "100dvh", sm: "min(720px, 90vh)" },
						m: 0,
						borderRadius: { xs: 0, sm: "16px" },
						overflow: "hidden",
						bgcolor: "#1a1a2e",
						p: 0,
						display: "flex",
					},
				}}
			>
				<AgoraVideoCallWithSnapshot
					onCallEnd={handleCallEnd}
					handleLossSubmit={handleLossSubmit}
					participantRole="Adjuster"
				/>
			</Dialog>

			{user?.role === "Maker" && (
				<Fab
					color="secondary"
					aria-label="add"
					onClick={handleCreateAssignment}
					sx={{
						position: "fixed",
						bottom: 16,
						left: 26,
					}}
				>
					<AddIcon />
				</Fab>
			)}

			<NotifyClaimantDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				currentAssignment={currentAssignment}
				selectedClaimantIdx={selectedClaimantIdx}
				onClaimantSelect={handleClaimantSelect}
				currentContact={currentContact}
				onContactChange={handleContactChange}
				errors={errors}
				isSubmitting={isSubmitting}
				onSubmit={handleNotifyYesClick}
			/>

			<AssignAdjusterDialog
				open={adjusterDialogOpen}
				onClose={() => setAdjusterDialogOpen(false)}
				users={users}
				loadingUsers={loadingUsers}
				onChange={handleChange}
				onAssign={AssignAdjusterToAssignment}
			/>

			<InvoiceDialog
				open={isShowInvoice}
				onClose={() => setIsShowInvoice(false)}
				onCloseInvoice={onCloseInvoice}
			/>

			<ImagePreviewDialog
				open={imageDialogOpen}
				onClose={handleCloseDialog}
				selectedImage={selectedImage}
			/>

			<SignatureDialog
				open={isShowCanvas}
				onClose={() => setIsShowCanvas(false)}
				onSubmit={() => setIsShowCanvas(false)}
				user={user}
				dataURLToBlob={dataURLToBlob}
				uploadSignature={uploadSignature}
			/>
		</Box>
	);
}

export default ExpandableList;
